import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Button, Input, Textarea } from '@/shared/ui';

import { AnalyticsSection } from '@/entities/analyticsModel';

import { analyticsModelMessages } from '../../config/i18n';

import styles from './AnalyticsSectionEditor.module.scss';

type AnalyticsSectionEditorProps = {
  id: string;
  section: AnalyticsSection;
  index: number;
  canEdit?: boolean;
  isEditing?: boolean;
  isInteractionDisabled?: boolean;
  className?: string;
  onChange: (updated: AnalyticsSection) => void;
  onRemove: () => void;
  onEdit?: () => void;
  onEditCode?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
};

export const AnalyticsSectionEditor: FC<AnalyticsSectionEditorProps> = (
  props,
) => {
  const {
    id,
    section,
    index,
    canEdit,
    isEditing,
    isInteractionDisabled,
    className,
    onChange,
    onEdit,
    onEditCode,
    onSave,
    onCancel,
    onRemove,
  } = props;

  const { formatMessage } = useIntl();
  const isDragDisabled = Boolean(
    !canEdit || isEditing || isInteractionDisabled,
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: isDragDisabled,
  });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };

  const handleFieldChange = useCallback(
    (field: keyof AnalyticsSection, value: string) => {
      onChange({ ...section, [field]: value });
    },
    [section, onChange],
  );

  if (!isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={sortableStyle}
        className={cn(
          styles.container,
          isDragging && styles.containerDragging,
          isInteractionDisabled && styles.containerDisabled,
          className,
        )}
      >
        <div className={styles.header}>
          <span
            className={cn(
              styles.numberLabel,
              !isDragDisabled && styles.numberLabelDraggable,
            )}
            {...(isDragDisabled ? {} : attributes)}
            {...(isDragDisabled ? {} : listeners)}
          >
            {index + 1}
          </span>
          <div className={styles.headerFields}>
            <div className={styles.titleCluster}>
              <div className={styles.readOnlyTitleRow}>
                <span className={styles.readOnlyName}>{section.name}</span>
              </div>
            </div>
          </div>
          {canEdit && (
            <div className={styles.actions}>
              <Button
                variant="tertiary"
                size="tiny"
                icon="pen"
                circle
                onClick={onEdit}
                disabled={isInteractionDisabled}
                title={formatMessage(analyticsModelMessages.edit)}
              />
              <Button
                variant="tertiary"
                size="tiny"
                icon="delete"
                circle
                className={styles.deleteButton}
                onClick={onRemove}
                disabled={isInteractionDisabled}
                title={formatMessage(analyticsModelMessages.delete)}
              />
            </div>
          )}
        </div>
        {section.instructions && (
          <p className={styles.readOnlyText}>{section.instructions}</p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      data-component="analytics-section-editor"
      className={cn(
        styles.container,
        styles.containerEditing,
        isDragging && styles.containerDragging,
        className,
      )}
    >
      <div className={styles.header}>
        <span className={styles.numberLabel}>{index + 1}</span>
        <div className={styles.editorBody}>
          <div className={styles.editorMainRow}>
            <div className={styles.headerFields}>
              <Input
                name={`sectionName-${index}`}
                value={section.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder={formatMessage(analyticsModelMessages.sectionName)}
                className={styles.nameInput}
              />
            </div>
            <div className={styles.primaryActions}>
              <Button variant="primary" size="small" onClick={onSave}>
                {formatMessage(analyticsModelMessages.save)}
              </Button>
              <Button variant="tertiary" size="small" onClick={onCancel}>
                {formatMessage(analyticsModelMessages.cancel)}
              </Button>
              {onEditCode && (
                <Button variant="tertiary" size="small" onClick={onEditCode}>
                  {formatMessage(analyticsModelMessages.advanced)}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.instructionsRow}>
        <Textarea
          value={section.instructions ?? ''}
          onChange={(e) => handleFieldChange('instructions', e.target.value)}
          placeholder={formatMessage(
            analyticsModelMessages.sectionInstructions,
          )}
          rows={2}
          className={styles.instructionsField}
        />
      </div>
    </div>
  );
};
